precision highp float;

uniform float uTime;
uniform vec2 uResolution;
uniform float uBass;
uniform float uMid;
uniform float uHigh;
uniform float uAudioAverage;
uniform float uZoom;
uniform vec2 uOffset;
uniform vec3 uColorPrimary;
uniform vec3 uColorSecondary;
uniform vec3 uColorAccent;
uniform vec2 uDeviceTilt;

varying vec2 vUv;

const int MAX_ITERATIONS = 120;

// Noise function for organic dissolution
float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}

float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  
  float a = hash(i);
  float b = hash(i + vec2(1.0, 0.0));
  float c = hash(i + vec2(0.0, 1.0));
  float d = hash(i + vec2(1.0, 1.0));
  
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm(vec2 p) {
  float value = 0.0;
  float amplitude = 0.5;
  float frequency = 1.0;
  
  for (int i = 0; i < 5; i++) {
    value += amplitude * noise(p * frequency);
    amplitude *= 0.5;
    frequency *= 2.0;
  }
  
  return value;
}

void main() {
  vec2 uv = (gl_FragCoord.xy - 0.5 * uResolution.xy) / min(uResolution.x, uResolution.y);
  
  // The dissolving effect - patterns fade into uniformity
  float dissolve = uZoom * 0.5; // Increases over time
  
  vec2 z = uv * 2.5 + uDeviceTilt * 0.3;
  vec2 c = vec2(-0.4, 0.6);
  
  float iterations = 0.0;
  
  for (int i = 0; i < MAX_ITERATIONS; i++) {
    if (dot(z, z) > 4.0) break;
    
    float xTemp = z.x * z.x - z.y * z.y + c.x;
    z.y = 2.0 * z.x * z.y + c.y;
    z.x = xTemp;
    
    iterations += 1.0;
  }
  
  float t = iterations / float(MAX_ITERATIONS);
  
  // Add noise-based dissolution
  float noiseVal = fbm(uv * 3.0 + uTime * 0.1);
  float dissolveMask = smoothstep(dissolve - 0.5, dissolve + 0.5, noiseVal + t);
  
  // The fractal pattern
  vec3 fractalColor;
  if (t >= 1.0) {
    fractalColor = uColorPrimary * 0.3;
  } else {
    float phase = t * 3.0 + uTime * 0.02;
    fractalColor = mix(uColorSecondary, uColorAccent, sin(phase) * 0.5 + 0.5);
    fractalColor *= 0.8 + 0.2 * t;
  }
  
  // The gradient "void" - uniform, featureless
  vec3 voidColor = uColorPrimary * 0.15;
  
  // Add very subtle variation to void (it's not complete nothingness)
  float voidNoise = noise(uv * 0.5 + uTime * 0.01) * 0.03;
  voidColor += vec3(voidNoise);
  
  // Blend between fractal and void based on dissolution
  vec3 col = mix(fractalColor, voidColor, dissolveMask);
  
  // At the boundary of dissolution, add a faint glow
  float boundary = smoothstep(0.0, 0.1, abs(dissolveMask - 0.5) * 2.0);
  boundary = 1.0 - boundary;
  col += uColorAccent * boundary * 0.2 * (1.0 + uMid);
  
  // Depth perception - the void seems to recede
  float depth = dissolveMask * 0.3;
  col *= 1.0 - depth;
  
  // Audio creates slight ripples in the void
  float ripple = sin(length(uv) * 10.0 - uTime * 2.0) * uBass * 0.05;
  col += vec3(ripple) * (1.0 - dissolveMask);
  
  // Overall fade to darker as patterns disappear
  col *= 0.8 + (1.0 - dissolveMask) * 0.4;
  
  // Gentle radial gradient suggesting infinity
  float radial = length(uv);
  col *= 1.0 - radial * 0.15;
  
  gl_FragColor = vec4(col, 1.0);
}

